import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport, CdkVirtualScrollableWindow } from "@angular/cdk/scrolling";
import { NgClass, NgStyle, NgTemplateOutlet, ViewportScroller } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    forwardRef,
    inject
} from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import _ from "lodash";
import { BehaviorSubject, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { StatedataService, Volatile } from "~app/common/services/statedata.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { CheckboxComponent } from "../checkbox/checkbox.component";
import { LabelComponent } from "../label/label.component";
import { ListColumnDirective } from "./list-column.directive";
import { ListCustomRowDirective } from "./list-custom-row.directive";
import { ListSorting, ListState } from "./list.models";

@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ListComponent)
        }
    ],
    selector: "rask-list",
    styleUrls: ["./list.component.scss"],
    templateUrl: "./list.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        LabelComponent,
        NgClass,
        NgTemplateOutlet,
        CdkVirtualScrollViewport,
        CdkVirtualScrollableWindow,
        CdkFixedSizeVirtualScroll,
        CheckboxComponent,
        CdkVirtualForOf,
        NgStyle
    ]
})
export class ListComponent implements OnChanges, OnDestroy {
    @ViewChild(CdkVirtualScrollViewport, { static: false }) cdkScroll: CdkVirtualScrollViewport;
    checkboxWidth = "40px";
    @Output() checkedChanged = new EventEmitter<boolean>();
    @Input() clientFilterResultText = "";
    @ContentChildren(ListColumnDirective) columns: QueryList<ListColumnDirective>;
    @ContentChildren(ListCustomRowDirective) customRows: QueryList<ListCustomRowDirective>;
    @Input() data: any[];
    dataSorted: any[];
    filteredData: any[];
    @Input() fixedHeader = false;
    getSelected = true;
    @Input() getSelectedFromRoute = false;
    @Input() innerHeader = false;
    @Input() key: string;
    listState: ListState;
    @Input() openFn = null;
    @Input() rowSelectable = false;
    searchString: string;
    selectAll = false;
    @Input() selected: string;
    @Input() showHeader = true;
    @Input() showHeaderWhenEmpty = true;
    @Input() sortOrder: string;
    sorting = false;
    term$ = new BehaviorSubject(null);
    @Input() useClientFilter = false;
    @Input() viewportHeight = "280px";
    @Input() virtualScroll = false;
    @Input() virtualScrollItemHeight?: number;

    /**
     * OBS om du använder "panel" så måste du använda viewportHeight för att sätta höjden, annars kommer höjden vara 0.
     */
    @Input() virtualScrollType: "panel"|"window" = "panel";
    volatile: Volatile;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly destroy$ = new Subject<void>();
    private readonly statedataService = inject(StatedataService);
    private readonly viewScroller = inject(ViewportScroller);

    constructor() {
        this.volatile = this.statedataService.getVolatile();
        this.listState = new ListState();
        this.term$.pipe(
            debounceTime(200),
            takeUntil(this.destroy$)
        ).subscribe(x => {
            this.performClientSearch();
            this.changeDetectorRef.detectChanges();
        });
    }

    // Specialare som används för att få till fixedHeaders om man kör med virtual scroll
    // Man måste invertera th-cellernas top-position baserat på translation som cdkvirtualscroll gör
    get inverseOfCdkScrollTranslation(): string {

        if (!this.fixedHeader || !this.cdkScroll) {
            return "-0px";
        }
        // Denna är en liten nödlösning nu. Kan tas bort när ordentlig border har fixats på thead/th
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.cdkScroll["_renderedContentOffset"]) {
            return "-1px";
        }
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const offset = this.cdkScroll["_renderedContentOffset"];
        return `-${offset + 1}px`;
    }

    get showTable() {
        return this.data || this.showHeaderWhenEmpty;
    }

    clear(): void {
        this.clearSelected();
        this.clearSorting();
    }

    clearSelected(): void {
        this.selected = null;
    }

    clearSorting(): void {
        this.listState.sorting = new ListSorting("", null);
    }

    generateId(index: number): string {
        if (this.key) {
            const keyValue = this.dataSorted[index][this.key];
            return this.generateRowId(keyValue);
        }

        return this.dataSorted[index].id ? this.generateRowId(this.dataSorted[index].id) : this.generateRowId(index);
    }

    generateRowId(val: any): string {
        return  `row_${val}`;
    }

    getSearchPlaceholder() {
        if(ObjectUtils.isNullOrUndefined(this.filteredData)) {
            return "";
        }
        return `${this.filteredData?.length} ${this.clientFilterResultText}`;
    }

    getSearchResultText() {
        return !ObjectUtils.stringHasValue(this.searchString) ? "" : `${this.filteredData?.length} träffar på "${this.searchString}" i sökresultatet`;
    }

    getSelectedFromRouteParam(): string {
        if (this.key) {
            return this.statedataService.previousParams ? this.statedataService.previousParams[this.key] : null;
        }

        return this.statedataService.previousParams ? this.statedataService.previousParams.id : null;
    }

    getSelectedRows(): any[] {
        return this.data != null ? this.data.filter(x => x.selected) : null;
    }

    getSortedData(): any[] {
        return this.dataSorted;
    }

    isSelected(index: number, last: boolean): boolean {
        const rowId = this.generateId(index);
        if (last && this.selected) {
            this.scrollTo(this.generateRowId(this.selected));
        }

        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (rowId === this.generateRowId(this.selected)) {
            return true;
        }

        return false;
    }

    isSortActive(column: ListColumnDirective): boolean {
        return this.listState.sorting && this.listState.sorting.field === column.field && column.sortable;
    }

    isSortReverse(column: ListColumnDirective): boolean | null {
        return this.listState.sorting.field === column.field ? this.listState.sorting.reverse : null;
    }

    ngOnChanges(changes: SimpleChanges) {
        const isDataChanged = changes.data;
        // Reset sorting when changing datasource
        if (isDataChanged && this.data) {
            this.filteredData = this.data.slice();
            this.dataSorted = this.filteredData;
            this.dataSorted.forEach(x => x.selected = false);
            this.listState = this.volatile.get<ListState>("sorting");
            this.applySorting();
            if(this.useClientFilter) {
                this.performClientSearch();
            }

            if (this.getSelectedFromRoute) {
                if (this.selected) {
                    this.selected = null;
                } else if (this.getSelected) {
                    this.selected = this.getSelectedFromRouteParam();
                    this.getSelected = false;
                }
            }

            if (this.rowSelectable) {
                this.selectAll = false;
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onSearchFilterInputChange = (newValue: string) => {
        this.term$.next(null);
    };

    open(row: any) {
        if (this.openFn) {
            this.openFn(row);
        }
    }

    removeItemFromSelectionList(filter) {
        const selectedItem = this.getSelectedRows().find(filter);
        selectedItem.selected = false;
        this.changeDetectorRef.detectChanges();
    }

    removeSearchFilter() {
        this.searchString = undefined;
        this.filteredData = this.data.slice();
        this.applySorting();
    }

    scrollTo(tag: string): void {
        this.viewScroller.scrollToAnchor(tag);
    }

    selectAllChanged(value: boolean) {
        this.dataSorted.forEach(x => x.selected = value);
        this.checkedChanged.emit(value);
    }

    selectRowChanged(value: boolean) {
        if (!value) {
            this.selectAll = value;
        }
        this.checkedChanged.emit(value);
    }

    setSortReverse(reverse?: boolean): boolean {
        if (reverse) {
            reverse = null;
        } else if (reverse === null) {
            reverse = false;
        } else {
            reverse = !reverse;
        }

        return reverse;
    }

    setSorting(column: ListColumnDirective): void {
        this.selected = null;
        if (!column.sortable) {
            return;
        }

        this.sorting = true;
        if (this.listState.sorting.field !== column.field) {
            this.listState.sorting.reverse = null;
        }

        this.listState.sorting.field = column.field;
        this.listState.sorting.reverse = this.setSortReverse(this.listState.sorting.reverse);
        if (this.listState.sorting.reverse === null) {
            this.dataSorted = this.data.slice();
        } else {
            this.dataSorted = this.sortBy(this.listState.sorting.field, !this.listState.sorting.reverse, column.sortString);
        }
        this.sorting = false;
        this.volatile.set("sorting", this.listState);
    }

    shouldShowRemoveFilter() {
        return  ObjectUtils.stringHasValue(this.searchString);
    }

    sortBy(prop: string, isAsc: boolean, customSortString?: (rowItem: any) => string): any[] {
        const sortedData = this.filteredData;
        if (!prop) {
            return sortedData;
        }

        if (customSortString) {
            return _.orderBy(sortedData, [customSortString], isAsc ? "asc" : "desc");
        }
        return _.orderBy(sortedData, [prop], isAsc ? "asc" : "desc");
    }

    trackById = (index: number, row: any): number => {
        if (this.key) {
            return row[this.key];
        }

        return row.id ? row.id : index;
    };

    private applySorting() {
        if (this.listState && this.listState.sorting) {
            this.dataSorted = this.sortBy(this.listState.sorting.field, !this.listState.sorting.reverse);
        } else {
            this.listState = new ListState();
        }
    }

    private performClientSearch() {
        if(!this.data) {
            return;
        }
        this.filteredData = this.data.slice();
        if(!ObjectUtils.isNullOrUndefined(this.searchString)) {
            this.filteredData = this.filteredData.filter(item => this.columns.some(k => item[k.field] != null &&
                item[k.field].toString().toLowerCase()
                    .includes(this.searchString.toLowerCase())));
        }
        this.applySorting();
    }
}
